import { useCallback, useEffect, useRef, useState } from "react";
import type { Coordinate } from "@/entities/tracking";
import { themeColor } from "@/shared/design-system";
import { loadGoogleMaps } from "@/shared/geo/googleMapsLoader";
import { mapStyleForTheme } from "@/shared/geo/mapTheme";
import { sliceFromDistanceM, sliceToDistanceM } from "@/shared/geo/polyline";
import { MapUnavailable } from "@/shared/ui";
import { resolveTheme, usePreferencesStore } from "@/shared/stores/preferencesStore";
import { useRouteFollower, type FollowerFrame } from "../hooks/useRouteFollower";
import { clearRiderIconCache, quantizeHeading, riderIcon } from "./riderIcon";

type Props = {
  agentLocation: Coordinate;
  pickupLocation: Coordinate;
  dropoffLocation: Coordinate;
  routePoints: Coordinate[];
  userLocation?: Coordinate | null;
  followAgent?: boolean;
  recenterTrigger?: number;
  onFatalError?: () => void;
};

/**
 * Carte de suivi — SDK Google Maps, directement dans le DOM.
 *
 * ## Ce que la migration supprime
 * L'app native avait TROIS implémentations pour un seul besoin :
 *   • `TrackingMap` (react-native-maps, Google natif) ;
 *   • `LeafletTrackingMap` (278 LOC) qui construisait une page HTML complète
 *     en chaîne de caractères, la chargeait dans une `WebView`, et poussait les
 *     mises à jour de position par `injectJavaScript` ;
 *   • `TrackingMapFallback` (224 LOC), un rendu SVG sans tuiles, affiché par
 *     `SmartTrackingMap` quand le module natif était absent (Expo Go).
 *
 * Sur web, tout cela s'effondre en un seul composant : plus de WebView à
 * alimenter, plus de HTML à générer, plus de pont à traverser, et plus de
 * raison qu'un module natif manque. `SmartTrackingMap` et son ErrorBoundary
 * disparaissent avec.
 *
 * ## Fournisseur : Google Maps
 * `routePoints` est déjà calculé par la Routes API. Avec un fond OpenStreetMap,
 * les deux réseaux routiers pouvaient diverger et la route se dessinait à côté
 * de la rue ; le même fournisseur des deux côtés supprime cet écart.
 *
 * ⚠️ Exige `VITE_GOOGLE_MAPS_API_KEY` avec **Maps JavaScript API** activée —
 * distincte de la Routes API.
 *
 * ## Mises à jour impératives, et RIEN d'autre
 * Les marqueurs sont créés une fois puis DÉPLACÉS (`setPosition`), jamais
 * recréés : les recréer ferait clignoter la carte et repartirait le rendu des
 * tuiles.
 *
 * L'animation va plus loin — elle ne passe par AUCUN état React. `useRouteFollower`
 * émet une frame soixante fois par seconde ; la faire transiter par `useState`
 * re-rendrait tout l'écran de suivi à cette cadence, avec ses effets à
 * re-comparer, pour ne changer au bout du compte qu'un attribut de marqueur.
 * Ici la frame arrive dans `applyFrame`, qui parle directement au SDK. Ce
 * composant ne se rend pas une seule fois pendant que la moto avance.
 */

const toLatLng = (c: Coordinate): google.maps.LatLngLiteral => ({
  lat: c.latitude,
  lng: c.longitude,
});

/**
 * Marqueurs dessinés en symboles SVG plutôt qu'en `AdvancedMarkerElement`.
 *
 * Les marqueurs avancés exigent un `mapId` déclaré dans la console Google —
 * une configuration de plus à tenir hors du dépôt, et une carte qui casse si
 * quelqu'un la supprime. Un symbole rend le même disque coloré, se contente de
 * la clé, et garde les couleurs dans les tokens du design system.
 *
 * ⚠️ N'appeler qu'APRÈS `loadGoogleMaps()` : `SymbolPath` est une valeur du
 * SDK, absente tant que le script n'est pas chargé.
 */
function circleSymbol(fill: string, radius: number, strokeWeight = 3): google.maps.Symbol {
  return {
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: fill,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight,
    scale: radius,
  };
}

/**
 * ⚠️ Les couleurs passent par `themeColor()`, jamais par `colors.*`.
 *
 * Les tokens valent désormais `var(--c-x)`. Le SDK Google Maps ne lit pas le
 * CSS : il parse lui-même la chaîne reçue dans `fillColor` / `strokeColor`, et
 * une variable non résolue ne dessine RIEN — marqueurs et tracé disparaissent
 * sans la moindre erreur en console. `themeColor()` résout la valeur à
 * l'instant T et rend un vrai `#rrggbb`.
 */

/**
 * Marqueur de la Main : une MOTO VUE DE DESSUS, avec son conducteur.
 *
 * Un simple disque ne dit pas OÙ va la moto — le client voyait un point bouger
 * sans savoir s'il venait vers lui ou s'en éloignait. L'icône s'aligne sur la
 * TANGENTE à la rue (cf. `useRouteFollower`) : elle tourne dans le virage, avec
 * lui, au lieu de pointer à travers.
 *
 * Le dessin lui-même vit dans `riderIcon` — il fallait trois couleurs, ce
 * qu'un `google.maps.Symbol` ne sait pas porter.
 *
 * Sans cap connu (course qui démarre, position sans route où se recaler), on
 * retombe sur le disque : une moto pointant arbitrairement vers le nord serait
 * une information fausse, et elle serait crue — un véhicule dessiné affirme
 * une direction bien plus fort qu'un point.
 */
function agentIcon(heading: number | null): google.maps.Icon | google.maps.Symbol {
  if (heading == null) return circleSymbol(themeColor("or500"), 11, 4);
  return riderIcon(heading);
}

/** Le trajet qui RESTE : plein, à la couleur de la marque. */
function aheadOptions(): google.maps.PolylineOptions {
  return { strokeColor: themeColor("or500"), strokeWeight: 6, strokeOpacity: 0.95, zIndex: 3 };
}

/**
 * Le trajet DÉJÀ PARCOURU : en retrait, derrière la Main.
 *
 * Une ligne unique ne dit pas où en est la course. Deux traits le disent d'un
 * coup d'œil : ce qui est fait s'efface, ce qui reste tient la couleur. C'est
 * la lecture qu'on a dans un VTC, et elle ne coûte qu'une seconde polyligne.
 */
function behindOptions(): google.maps.PolylineOptions {
  return { strokeColor: themeColor("gray300"), strokeWeight: 5, strokeOpacity: 0.7, zIndex: 2 };
}

export function TrackingMap({
  agentLocation,
  pickupLocation,
  dropoffLocation,
  routePoints,
  userLocation,
  followAgent = true,
  recenterTrigger,
  onFatalError,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const layersRef = useRef<{
    agent?: google.maps.Marker;
    pickup?: google.maps.Marker;
    dropoff?: google.maps.Marker;
    user?: google.maps.Marker;
    ahead?: google.maps.Polyline;
    behind?: google.maps.Polyline;
  }>({});
  const [failed, setFailed] = useState(false);

  // Dernière frame reçue — rejouée telle quelle quand la carte finit de se
  // charger, ou quand le thème change. Sans elle, une carte qui arrive après le
  // premier mouvement afficherait la Main à sa position de départ.
  const lastFrameRef = useRef<FollowerFrame | null>(null);

  /**
   * Le client a-t-il pris la main sur la carte ?
   *
   * Recentrer à chaque frame rend le suivi confortable, mais rend aussi tout
   * déplacement manuel impossible : la carte revient sur la moto avant même que
   * le doigt ne soit levé. Dès que le client fait glisser la carte, on cesse de
   * la recentrer — il regarde autre chose, et c'est légitime. Le bouton
   * « Recentrer » rend le suivi.
   */
  const userTookOverRef = useRef(false);
  const dragListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  /** Dernier cap quantifié POSÉ sur le marqueur — `null` tant qu'inconnu. */
  const iconKeyRef = useRef<number | null>(null);

  const followAgentRef = useRef(followAgent);
  followAgentRef.current = followAgent;

  /**
   * Pousse une frame d'animation dans le SDK. Appelée ~60 fois par seconde,
   * hors de React : aucun rendu, aucun effet rejoué.
   */
  const applyFrame = useCallback((frame: FollowerFrame) => {
    lastFrameRef.current = frame;

    const marker = layersRef.current.agent;
    if (!marker) return;

    marker.setPosition(toLatLng(frame.coord));

    // ⚠️ L'icône n'est reposée QUE si le cap a bougé d'un cran.
    //
    // La position, elle, change à chaque frame — c'est le glissement. Mais
    // l'icône est désormais une IMAGE : la reposer soixante fois par seconde
    // ferait redécoder un SVG à chaque frame, pour un dessin identique. Le cap
    // quantifié (3°) sert de clé : entre deux crans, il n'y a rien à faire.
    const nextKey = frame.heading == null ? null : quantizeHeading(frame.heading);
    if (nextKey !== iconKeyRef.current) {
      iconKeyRef.current = nextKey;
      marker.setIcon(agentIcon(frame.heading));
    }

    // Découpe du tracé au niveau de la Main. `alongM` est null quand elle n'est
    // pas recalée sur la route (écart trop grand, itinéraire indisponible) :
    // on montre alors le tracé entier plutôt qu'une découpe fausse.
    const { ahead, behind } = layersRef.current;
    if (ahead && behind && frame.measured) {
      if (frame.alongM != null) {
        ahead.setPath(sliceFromDistanceM(frame.measured, frame.alongM).map(toLatLng));
        behind.setPath(sliceToDistanceM(frame.measured, frame.alongM).map(toLatLng));
      } else {
        ahead.setPath(frame.measured.points.map(toLatLng));
        behind.setPath([]);
      }
    }

    const map = mapRef.current;
    // `setCenter` et non `panTo` : `panTo` lance sa PROPRE animation de caméra,
    // qui entrerait en concurrence avec la nôtre soixante fois par seconde —
    // la carte tremblerait. Le déplacement étant déjà lissé, un recentrage sec
    // par frame donne un glissement parfaitement continu.
    if (map && followAgentRef.current && !userTookOverRef.current) {
      map.setCenter(toLatLng(frame.coord));
    }
  }, []);

  useRouteFollower(agentLocation, routePoints, applyFrame);

  // Props les plus récentes, lues à l'initialisation asynchrone : le SDK peut
  // mettre une seconde à arriver, pendant laquelle l'agent a déjà bougé.
  const latestRef = useRef({ agentLocation, pickupLocation, dropoffLocation, routePoints });
  latestRef.current = { agentLocation, pickupLocation, dropoffLocation, routePoints };

  const onFatalErrorRef = useRef(onFatalError);
  onFatalErrorRef.current = onFatalError;

  // ── Création de la carte (une seule fois) ────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    void loadGoogleMaps()
      .then((maps) => {
        const el = containerRef.current;
        if (cancelled || !el || mapRef.current) return;

        const { agentLocation: a, pickupLocation: p, dropoffLocation: d, routePoints: r } =
          latestRef.current;

        const map = new maps.Map(el, {
          // Tuiles assombries en thème sombre : sans style, Google rend une
          // carte blanche éclatante au milieu d'une interface sombre.
          styles: mapStyleForTheme(resolveTheme(usePreferencesStore.getState().theme)),
          center: toLatLng(a),
          zoom: 15,
          // Contrôles par défaut désactivés : l'app fournit ses propres boutons,
          // et l'écran mobile n'a pas la place.
          disableDefaultUI: true,
          gestureHandling: "greedy",
          clickableIcons: false,
        });

        // Le client fait glisser la carte : il regarde ailleurs, on lui laisse
        // la main jusqu'au prochain « Recentrer ».
        dragListenerRef.current = map.addListener("dragstart", () => {
          userTookOverRef.current = true;
        });

        layersRef.current.behind = new maps.Polyline({ ...behindOptions(), path: [], map });
        layersRef.current.ahead = new maps.Polyline({
          ...aheadOptions(),
          path: r.map(toLatLng),
          map,
        });

        layersRef.current.pickup = new maps.Marker({
          position: toLatLng(p),
          icon: circleSymbol(themeColor("navy800"), 7),
          map,
        });
        layersRef.current.dropoff = new maps.Marker({
          position: toLatLng(d),
          icon: circleSymbol(themeColor("success600"), 7),
          map,
        });
        layersRef.current.agent = new maps.Marker({
          position: toLatLng(a),
          // Cap encore inconnu au montage → disque. La première frame pose la
          // flèche orientée.
          icon: agentIcon(null),
          zIndex: 10, // la Main passe toujours au-dessus des points fixes
          map,
        });

        mapRef.current = map;

        // L'animation a pu démarrer avant que le SDK n'arrive : on rejoue sa
        // dernière frame, sinon la Main resterait à sa position de départ
        // jusqu'au ping suivant — plusieurs secondes de carte fausse.
        if (lastFrameRef.current) applyFrame(lastFrameRef.current);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        console.error("[TrackingMap] initialisation Google Maps échouée", e);
        setFailed(true);
        onFatalErrorRef.current?.();
      });

    return () => {
      cancelled = true;
      // Pas de `destroy()` dans le SDK : on détache l'écouteur et les couches,
      // le conteneur DOM part avec le composant.
      dragListenerRef.current?.remove();
      dragListenerRef.current = null;
      Object.values(layersRef.current).forEach((layer) => layer?.setMap(null));
      layersRef.current = {};
      mapRef.current = null;
    };
    // Volontairement sans dépendances : la carte se crée une fois, les mises à
    // jour passent par les effets ci-dessous. Les positions initiales sont lues
    // via `latestRef` — d'où la disparition du `eslint-disable` qu'exigeait la
    // version Leaflet, qui les lisait directement dans la portée.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Bascule de thème ─────────────────────────────────────────────────────
  // Les couleurs ont été FIGÉES dans le SDK à la création des couches : elles
  // ne suivent pas le changement de thème, contrairement au reste de l'app. On
  // les réapplique, sinon la carte garde les teintes du thème précédent.
  const themePreference = usePreferencesStore((s) => s.theme);
  useEffect(() => {
    const { ahead, behind, pickup, dropoff, user, agent } = layersRef.current;
    if (!ahead) return;

    ahead.setOptions(aheadOptions());
    behind?.setOptions(behindOptions());
    pickup?.setIcon(circleSymbol(themeColor("navy800"), 7));
    dropoff?.setIcon(circleSymbol(themeColor("success600"), 7));
    user?.setIcon(circleSymbol(themeColor("navy600"), 6));
    // Le blouson du conducteur vient de `themeColor()`, figé à la construction
    // de l'image : sans purge, la moto garderait l'or du thème précédent.
    clearRiderIconCache();
    iconKeyRef.current = null;
    agent?.setIcon(agentIcon(lastFrameRef.current?.heading ?? null));
  }, [themePreference]);

  // ── Départ / arrivée ─────────────────────────────────────────────────────
  useEffect(() => {
    layersRef.current.pickup?.setPosition(toLatLng(pickupLocation));
    layersRef.current.dropoff?.setPosition(toLatLng(dropoffLocation));
  }, [pickupLocation, dropoffLocation]);

  // ── Nouveau tracé ────────────────────────────────────────────────────────
  // La découpe parcouru/restant est faite par `applyFrame`, à chaque frame. Cet
  // effet ne sert qu'au cas où aucune frame n'est encore arrivée : sans lui, un
  // itinéraire calculé avant la première position ne s'afficherait pas.
  useEffect(() => {
    if (lastFrameRef.current) return;
    layersRef.current.ahead?.setPath(routePoints.map(toLatLng));
  }, [routePoints]);

  // ── Position de l'utilisateur (optionnelle) ──────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!userLocation) {
      layersRef.current.user?.setMap(null);
      layersRef.current.user = undefined;
      return;
    }
    if (layersRef.current.user) {
      layersRef.current.user.setPosition(toLatLng(userLocation));
    } else {
      layersRef.current.user = new google.maps.Marker({
        position: toLatLng(userLocation),
        icon: circleSymbol(themeColor("navy600"), 6),
        map,
      });
    }
  }, [userLocation]);

  // ── Recentrage programmatique ────────────────────────────────────────────
  // `recenterTrigger` est un compteur : il rejoue l'action même si les
  // coordonnées n'ont pas changé (bouton « recentrer » appuyé deux fois).
  useEffect(() => {
    if (recenterTrigger == null) return;
    const map = mapRef.current;
    if (!map) return;

    // Le client redemande le suivi : on reprend la caméra.
    userTookOverRef.current = false;

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(toLatLng(lastFrameRef.current?.coord ?? agentLocation));
    bounds.extend(toLatLng(pickupLocation));
    bounds.extend(toLatLng(dropoffLocation));
    map.fitBounds(bounds, 48);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recenterTrigger]);

  if (failed) return <MapUnavailable />;

  // Fond visible le temps que les tuiles arrivent — un token, pas un vert en
  // dur, sinon il flashe en clair sur une interface sombre.
  return <div ref={containerRef} className="h-full w-full bg-gray-100" />;
}
